import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../api/client';
import './TemplateEventsWidget.css';
import { translateError } from '../utils/errorMessages';

// =============================================================================
// TYPES
// =============================================================================

interface TemplateEvent {
  _id: string;
  name: string;
  startTime: string;
  endTime: string;
  hasManualChanges: boolean;
  attendanceStats?: {
    attending: number;
    notAttending: number;
    maybe: number;
    total: number;
  };
}

interface TemplateEventsResponse {
  events: TemplateEvent[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  };
}

interface TemplateEventsWidgetProps {
  templateId: string;
}

// =============================================================================
// COMPONENT
// =============================================================================

const TemplateEventsWidget: React.FC<TemplateEventsWidgetProps> = ({ templateId }) => {
  const { t, i18n } = useTranslation();
  const [events, setEvents] = useState<TemplateEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [futureOnly, setFutureOnly] = useState(true);

  const fetchEvents = useCallback(async () => {
    if (!templateId) return;

    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: '10',
        futureOnly: String(futureOnly),
      });

      const response = await apiClient.get<TemplateEventsResponse>(
        `/api/event-templates/${templateId}/events?${params}`
      );

      setEvents(response.data.events);
      setTotalPages(response.data.pagination.pages);
      setTotal(response.data.pagination.total);
    } catch (err: any) {
      setError(translateError(err, t));
    } finally {
      setLoading(false);
    }
  }, [templateId, page, futureOnly]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // Reset to page 1 when filter changes
  useEffect(() => {
    setPage(1);
  }, [futureOnly]);

  const locale = i18n.language === 'cs' ? 'cs-CZ' : 'en-US';

  const formatDateTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(locale, {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatTime = (dateStr: string): string => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const handlePrevPage = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNextPage = () => {
    if (page < totalPages) setPage(page + 1);
  };

  return (
    <div className="template-events-widget">
      {/* Filter Toggle */}
      <div className="template-events-widget__filter">
        <button
          type="button"
          className={`template-events-widget__filter-btn ${futureOnly ? 'active' : ''}`}
          onClick={() => setFutureOnly(true)}
        >
          {t('templateEvents.futureOnly', 'Future only')}
        </button>
        <button
          type="button"
          className={`template-events-widget__filter-btn ${!futureOnly ? 'active' : ''}`}
          onClick={() => setFutureOnly(false)}
        >
          {t('templateEvents.showAll', 'Show all')}
        </button>
      </div>

      {/* Content */}
      {loading ? (
        <div className="template-events-widget__loading">
          {t('common.loading')}
        </div>
      ) : error ? (
        <div className="template-events-widget__error">
          {t('common.error')}: {error}
        </div>
      ) : events.length === 0 ? (
        <div className="template-events-widget__empty">
          {t('templateEvents.noEvents', 'No events found')}
        </div>
      ) : (
        <>
          <div className="template-events-widget__summary">
            {t('templateEvents.totalEvents', '{{count}} events', { count: total })}
          </div>

          <ul className="template-events-widget__list">
            {events.map((event) => (
              <li key={event._id} className="template-events-widget__item">
                <Link
                  to={`/admin/events/${event._id}/edit`}
                  className="template-events-widget__link"
                >
                  <div className="template-events-widget__date">
                    {formatDateTime(event.startTime)}
                    <span className="template-events-widget__time-separator">-</span>
                    {new Date(event.startTime).toDateString() !== new Date(event.endTime).toDateString()
                      ? formatDateTime(event.endTime)
                      : formatTime(event.endTime)}
                  </div>
                  <div className="template-events-widget__meta">
                    {event.attendanceStats && (
                      <span className="template-events-widget__attendance">
                        {event.attendanceStats.attending} / {event.attendanceStats.total}
                        {' '}
                        {t('templateEvents.attending', 'attending')}
                      </span>
                    )}
                    {event.hasManualChanges && (
                      <span
                        className="template-events-widget__manual-icon"
                        title={t('templateEvents.hasManualChanges', 'Has manual changes')}
                      >
                        *
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            ))}
          </ul>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="template-events-widget__pagination">
              <button
                type="button"
                className="template-events-widget__page-btn"
                onClick={handlePrevPage}
                disabled={page <= 1}
              >
                &lt;
              </button>
              <span className="template-events-widget__page-info">
                {page} / {totalPages}
              </span>
              <button
                type="button"
                className="template-events-widget__page-btn"
                onClick={handleNextPage}
                disabled={page >= totalPages}
              >
                &gt;
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TemplateEventsWidget;
